import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useBookDetail } from '../hooks/useBookDetail'
import { updateBook, deleteBook, retryGeneration } from '../lib/api'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import GenerationStatusBadge from '../components/GenerationStatusBadge'
import ConfirmDialog from '../components/ConfirmDialog'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import type { MasteryLevel } from '../types/models'
import { Eyebrow, Plant, type Stage } from '../components/ui'

const STAGE: Record<MasteryLevel, Stage> = { SEED: 0, SPROUT: 1, SAPLING: 2, FOREST: 3 }

export default function BookDetail() {
  const { bookId } = useParams<{ bookId: string }>()
  const navigate = useNavigate()
  const { book, loading, error, refresh } = useBookDetail(bookId ?? '')

  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editAuthor, setEditAuthor] = useState('')
  const [editReadDate, setEditReadDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [tab, setTab] = useState<'chapters' | 'concepts'>('chapters')

  function startEdit() {
    if (!book) return
    setEditTitle(book.title)
    setEditAuthor(book.author)
    setEditReadDate(book.readDate ? book.readDate.slice(0, 10) : '')
    setSaveError(null)
    setEditing(true)
  }

  async function handleSave() {
    if (!bookId) return
    setSaving(true)
    setSaveError(null)
    try {
      await updateBook(bookId, {
        title: editTitle,
        author: editAuthor,
        readDate: editReadDate || undefined,
      })
      await refresh()
      setEditing(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!bookId) return
    setDeleting(true)
    try {
      await deleteBook(bookId)
      navigate('/library')
    } catch (e) {
      setDeleting(false)
      setShowDelete(false)
      setSaveError(e instanceof Error ? e.message : 'Failed to delete book')
    }
  }

  async function handleRetry() {
    if (!bookId || !book) return
    setRetrying(true)
    try {
      await retryGeneration(bookId)
      await refresh()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to retry generation')
    } finally {
      setRetrying(false)
    }
  }

  if (loading) return <LoadingSpinner />

  if (error || !book) {
    return (
      <Box>
        <ErrorMessage message={error ?? 'Book not found'} />
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/library')} sx={{ mt: 1 }}>
          Back to Library
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/library')} sx={{ mb: 2 }}>
        Back to Library
      </Button>

      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent>
          {editing ? (
            <Stack spacing={2}>
              <TextField label="Title" value={editTitle} onChange={e => setEditTitle(e.target.value)} fullWidth size="small" />
              <TextField label="Author" value={editAuthor} onChange={e => setEditAuthor(e.target.value)} fullWidth size="small" />
              <TextField
                label="Read date"
                type="date"
                value={editReadDate}
                onChange={e => setEditReadDate(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
              {saveError && <ErrorMessage message={saveError} />}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Plant stage={STAGE[book.masteryLevel]} size={52} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 28, letterSpacing: '-0.5px', lineHeight: 1.15 }}>
                  {book.title}
                </Typography>
                <Typography sx={{ color: 'var(--ink-2)', mt: 0.5 }}>by {book.author}</Typography>
                <Box sx={{ mt: 1 }}>
                  <GenerationStatusBadge status={book.questionGenerationStatus} />
                </Box>
              </Box>
              <IconButton onClick={startEdit} size="small"><EditIcon fontSize="small" /></IconButton>
            </Box>
          )}
        </CardContent>
      </Card>

      {saveError && !editing && <ErrorMessage message={saveError} />}

      <Box sx={{ mb: 3 }}>
        <Eyebrow>
          {book.masteredQuestionsCount} mastered · {book.learningQuestionsCount} learning · {book.newQuestionsCount} new
        </Eyebrow>
      </Box>

      {(() => {
        // Both fields are optional on the wire: the backend omits them rather than sending [].
        const chapters = book.chapters ?? []
        const concepts = book.concepts ?? []
        const hasChapters = chapters.length > 0
        // Mirror mobile: hide the toggle entirely when there are no chapters and show concepts directly.
        const activeTab = hasChapters ? tab : 'concepts'
        return (
          <>
            {hasChapters ? (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activeTab}
                onChange={(_, v) => { if (v) setTab(v) }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="chapters" sx={{ textTransform: 'none', px: 2 }}>Chapters</ToggleButton>
                <ToggleButton value="concepts" sx={{ textTransform: 'none', px: 2 }}>Concepts</ToggleButton>
              </ToggleButtonGroup>
            ) : (
              <Box sx={{ mb: 1 }}><Eyebrow moss>Concepts</Eyebrow></Box>
            )}

            {activeTab === 'chapters' ? (
              <Card elevation={1} sx={{ mb: 3 }}>
                <List disablePadding>
                  {chapters.map((ch, i) => (
                    <Box key={i}>
                      {i > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={ch.title}
                          secondary={
                            <>
                              {ch.highlightCount} highlight{ch.highlightCount !== 1 ? 's' : ''} ·{ch.newQuestionsCount} new · {ch.learningQuestionsCount} learning · {ch.masteredQuestionsCount} mastered
                            </>
                          }
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              </Card>
            ) : concepts.length === 0 ? (
              // No highlights to synthesise from: nothing more is coming. Mirrors mobile's NO_SOURCE_MATERIAL.
              book.questionGenerationStatus === 'COMPLETED' && book.highlightCount === 0 && book.orphanHighlightCount === 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    We couldn’t create learning concepts for this book. Try another book.
                  </Typography>
                  <Button variant="outlined" onClick={() => navigate('/library/add')}>
                    Add another book
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No concepts yet. We haven't grouped this book's highlights into concepts yet, check back after your next import or generation run.
                </Typography>
              )
            ) : (
              <Card elevation={1} sx={{ mb: 3 }}>
                <List disablePadding>
                  {concepts.map((c, i) => (
                    <Box key={c.id}>
                      {i > 0 && <Divider />}
                      <ListItem>
                        <Box sx={{ mr: 1.5, display: 'flex' }}>
                          <Plant stage={STAGE[c.masteryLevel]} size={28} soil={false} />
                        </Box>
                        <ListItemText
                          primary={c.name}
                          secondary={`${c.highlightCount} highlight${c.highlightCount !== 1 ? 's' : ''}`}
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              </Card>
            )}

            {activeTab === 'concepts' && book.orphanHighlightCount > 0 && (
              <Card elevation={0} variant="outlined" sx={{ mb: 3, borderStyle: 'dashed' }}>
                <ListItem>
                  <ListItemText
                    primary={`${book.orphanHighlightCount} highlight${book.orphanHighlightCount !== 1 ? 's' : ''} not yet sorted`}
                    secondary="These haven't been grouped into a concept yet."
                    slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 600 } } }}
                  />
                </ListItem>
              </Card>
            )}
          </>
        )
      })()}

      <Stack direction="row" spacing={1}>
        {book.questionGenerationStatus === 'FAILED' && (
          <Button variant="outlined" onClick={handleRetry} disabled={retrying}>
            {retrying ? <CircularProgress size={18} /> : 'Retry question generation'}
          </Button>
        )}
        <Button variant="outlined" color="error" onClick={() => setShowDelete(true)}>
          Delete book
        </Button>
      </Stack>

      {showDelete && (
        <ConfirmDialog
          title="Delete book"
          message={`Are you sure you want to delete "${book.title}"? All questions and review history will be lost.`}
          confirmLabel={deleting ? 'Deleting...' : 'Delete'}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
          dangerous
        />
      )}
    </Box>
  )
}
